import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import Certificates from './components/Certificates/Certificates';
import CertificatesFilter from './components/Filters/CertificatesFilter';
import { getApiV1Url } from './config/api';

interface VoluntaryRow {
  cert_reg_number: string;
  comment_apts: string;
  cert_link: string;
  cert_schema: string;
  cert_status: string;
  cert_reg_data: string;
  cert_finish_data: string;
  manufacture_inn: string;
  manufacture_fullname: string;
  product_fullname: string;
  regulatory_documentation: string;
  lan_name_1: string;
  lab_number_1: string;
  os_number: string;
  os_name: string;
  region_manufacturer: string;
}

function VoluntaryCertificatesTable() {
  const [items, setItems] = useState<VoluntaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  // Состояния фильтров     
  const [filteredAptsComments, setFilteredAptsComments] = useState<string[]>([]);
  const [filteredCertStatuses, setFilteredCertStatuses] = useState<string[]>([]);
  const [filteredYears, setFilteredYears] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [endDateRange, setEndDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [sortField, setSortField] = useState<keyof VoluntaryRow | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filters, setFilters] = useState({
    cert_reg_number: [] as string[],
    os_name: [] as string[],
    os_number: [] as string[],
    manufacture_inn: [] as string[],
    cert_reg_data: [] as string[],
    manufacture_fullname: [] as string[],
    region_manufacturer: [] as string[],
    lab_names: [] as string[]
  });

  const STORAGE_KEY = 'embed_voluntary_certificates_filters_v1';

  useEffect(() => {
    axios
      .get(getApiV1Url('VOLUNTARY_CERTIFICATES'))
      .then((res) => {
        const source: any[] = Array.isArray(res.data) ? res.data : [];
        const mapped: VoluntaryRow[] = source.map((v: any) => ({
          comment_apts: v.apts_comment || '',
          cert_reg_data: v.certificate_registration_date || '',
          cert_finish_data: v.certificate_expiry_date || '',
          cert_status: v.certificate_validity_period || '',
          cert_schema: v.sds_name || '',
          cert_reg_number: v.certificate_number || '',
          cert_link: v.certificate_link || '',
          manufacture_inn: v.inn_unp ? String(v.inn_unp) : '',
          manufacture_fullname: v.manufacturer || '',
          product_fullname: v.product || '',
          regulatory_documentation: v.regulatory_document || '',
          lan_name_1: v.laboratory_name || '',
          lab_number_1: (v.accreditation_certificate || '').toString().trim(),
          os_number: v.os_code || '',
          os_name: v.os_name || '',
          region_manufacturer: '',
        }));
        setItems(mapped);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  // Загрузка сохраненных фильтров
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) || {};
      const ensureArray = (v: any) => Array.isArray(v) ? v : [];
      setFilters(prev => ({
        cert_reg_number: ensureArray(saved.filters?.cert_reg_number),
        os_name: ensureArray(saved.filters?.os_name),
        os_number: ensureArray(saved.filters?.os_number),
        manufacture_inn: ensureArray(saved.filters?.manufacture_inn),
        cert_reg_data: ensureArray(saved.filters?.cert_reg_data),
        manufacture_fullname: ensureArray(saved.filters?.manufacture_fullname),
        region_manufacturer: ensureArray(saved.filters?.region_manufacturer),
        lab_names: ensureArray(saved.filters?.lab_names),
      }));
      setFilteredAptsComments(ensureArray(saved.filteredAptsComments));
      setFilteredCertStatuses(ensureArray(saved.filteredCertStatuses));
      setFilteredYears(ensureArray(saved.filteredYears));
      setDateRange({ start: saved.dateRange?.start || '', end: saved.dateRange?.end || '' });
      setEndDateRange({ start: saved.endDateRange?.start || '', end: saved.endDateRange?.end || '' });
      setSortField(saved.sortField || null);
      setSortDirection(saved.sortDirection || 'asc');
    } catch {}
  }, []);

  // Сохранение фильтров при изменении
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        filters,
        filteredAptsComments,
        filteredCertStatuses,
        filteredYears,
        dateRange,
        endDateRange,
        sortField,
        sortDirection,
      }));
    } catch {}
  }, [filters, filteredAptsComments, filteredCertStatuses, filteredYears, dateRange, endDateRange, sortField, sortDirection]);

  // Закрытие модалки через Esc
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isFilterOpen) {
          setIsFilterOpen(false);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFilterOpen]);

  // Функция сортировки
  const sortData = useCallback((data: VoluntaryRow[]) => {
    if (!sortField) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (sortField === 'manufacture_inn') {
        const aNum = parseFloat(aValue) || 0;
        const bNum = parseFloat(bValue) || 0;
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }

      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      
      if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortField, sortDirection]);

  const filteredItems = useMemo(() => {
    const normalizeText = (text: string) => text?.toLowerCase().trim() || '';

    let filteredData = [...items];

    // Фильтрация по диапазону дат регистрации
    if (dateRange.start || dateRange.end) {
      filteredData = filteredData.filter(item => {
        if (!item.cert_reg_data) return false;
        
        const itemDate = new Date(item.cert_reg_data);
        
        // Если указана начальная дата
        if (dateRange.start) {
          const startDate = new Date(dateRange.start);
          if (itemDate < startDate) return false;
        }
        
        // Если указана конечная дата
        if (dateRange.end) {
          const endDate = new Date(dateRange.end);
          if (itemDate > endDate) return false;
        }
        
        return true;
      });
    }

    // Фильтрация по диапазону дат окончания
    if (endDateRange.start || endDateRange.end) {
      filteredData = filteredData.filter(item => {
        if (!item.cert_finish_data) return false;
        
        const itemDate = new Date(item.cert_finish_data);
        
        // Если указана начальная дата
        if (endDateRange.start) {
          const startDate = new Date(endDateRange.start);
          if (itemDate < startDate) return false;
        }
        
        // Если указана конечная дата
        if (endDateRange.end) {
          const endDate = new Date(endDateRange.end);
          if (itemDate > endDate) return false;
        }
        
        return true;
      });
    }

    if (filteredYears.length > 0) {
      filteredData = filteredData.filter(item =>
        item.cert_reg_data && filteredYears.includes(item.cert_reg_data.slice(0, 4))
      );
    }

    filteredData = filteredData.filter(cert => {
      const matchesAptsComment = filteredAptsComments.length === 0 ||
        filteredAptsComments.some(comment =>
          normalizeText(cert.comment_apts) === normalizeText(comment)
        );

      const matchesCertStatus = filteredCertStatuses.length === 0 ||
        filteredCertStatuses.some(status =>
          normalizeText(cert.cert_status) === normalizeText(status)
        );

      // Фильтрация по лабораториям
      const matchesLabNames = filters.lab_names.length === 0 ||
        filters.lab_names.some(filterLabName => {
          const labName = cert.lan_name_1;
          return labName && labName !== 'Отсутствует' && normalizeText(labName).includes(normalizeText(filterLabName));
        });

      const matchesFilters = Object.entries(filters).every(([key, values]) => {
        if (key === 'lab_names') return true; 
        if (values.length === 0) return true;
        const fieldValue = (cert as any)[key];
        return values.some(value =>
          normalizeText(fieldValue).includes(normalizeText(value))
        );
      });

      return matchesAptsComment && matchesCertStatus && matchesLabNames && matchesFilters;
    });

    return sortData(filteredData);
  }, [items, filteredAptsComments, filteredCertStatuses, filteredYears, filters, dateRange, endDateRange, sortData]);

  // Обработчики фильтров
  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
  };

  const handleChartFilterChange = (newFilters: {
    aptsComments: string[];
    certStatuses: string[];
    years: string[];
  }) => {
    setFilteredAptsComments(newFilters.aptsComments);
    setFilteredCertStatuses(newFilters.certStatuses);
    setFilteredYears(newFilters.years);
  };

  const handleDateRangeChange = (field: 'cert_reg_data' | 'cert_finish_data', startDate: string, endDate: string) => {
    if (field === 'cert_reg_data') {
      setDateRange({ start: startDate, end: endDate });
    } else if (field === 'cert_finish_data') {
      setEndDateRange({ start: startDate, end: endDate });
    }
  };

  // Обработчик сортировки
  const handleSort = useCallback((field: any) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        // При третьем нажатии сброс
        setSortField(null);
        setSortDirection('asc');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField, sortDirection]);

  const getSortIcon = useCallback((field: any) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  }, [sortField, sortDirection]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 9999
      }}>
        <img
          src="/loading.png"
          alt="Загрузка..."
          style={{ width: 200, height: 200, animation: 'spin 1.2s linear infinite' }}
        />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  const noResults = filteredItems.length === 0;

  return (
    <div style={{ padding: 16 }} data-embed="true">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2>Реестр добровольных сертификатов</h2>
      </div>
      
      {isFilterOpen && (
        <div className="certificates-filter-modal-overlay">
          <div className="certificates-filter-modal" style={{ overflowY: 'visible' }}>
            <button className="certificates-filter-modal__close" onClick={() => setIsFilterOpen(false)}>
              ×
            </button>
            <CertificatesFilter
              certificates={items as any}
              onFilterChange={handleFilterChange}
              chartFilters={{
                aptsComments: filteredAptsComments,
                certStatuses: filteredCertStatuses,
                years: filteredYears
              }}
              onChartFilterChange={handleChartFilterChange}
              hideApplicantFields={true}
              onDateRangeChange={handleDateRangeChange}
              initialFilters={filters}
              initialDateRanges={{ cert_reg_data: dateRange, cert_finish_data: endDateRange }}
            />
          </div>
        </div>
      )}
      
      {noResults ? (
        <div style={{
          marginTop: 24,
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          padding: 24,
          textAlign: 'center',
          color: '#374151'
        }}>
          <h3 style={{ margin: 0, marginBottom: 8 }}>Нет результатов</h3>
          <div>По выбранным фильтрам не найдено данных. Измените параметры и попробуйте снова.</div>
        </div>
      ) : (
        <Certificates 
          filteredLabList={filteredItems as any} 
          onSort={handleSort} 
          getSortIcon={getSortIcon} 
          onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
          isFilterOpen={isFilterOpen}
          hideApplicantFields={true}
        />
      )}
    </div>
  );
}

export default VoluntaryCertificatesTable;


